import { useEffect, useState } from 'react';
import type { PhaseProgressUpdate } from '@volley/crucible-ipc-types/ipc-channels';

/**
 * Coarse re-render clock for the run-state stale-build / stale-final-review
 * escalations (#3025).
 *
 * `deriveWorkspaceRunState` reads `signals.nowMs` from this clock to (a) fire
 * the 15-minute stale-build escalation and (b) age the lastActivityAt stamp in
 * `activityStampProvesLiveness`. Both escalations are stamp/timestamp-vs-now
 * comparisons, so the clock MUST advance for every phase that can raise one —
 * otherwise a suppressed escalation never re-fires when the build actually goes
 * quiet (the mirror of #2917 at the run-state level).
 *
 * Round-3 fix: the escalation range is implementation (phases 3-5) AND final
 * review (phase 6). The previous inline clock advanced only during
 * implementation, so at phase 6 it froze — a fresh stamp suppressed the
 * final-review escalation indefinitely. Extracted from Workspace so the
 * "advances at phase 6 with no new props" property is unit-testable in
 * isolation (the inline clock could only be exercised by rendering all of
 * Workspace).
 */
/** True when a raw phase number is in the stamp-gated escalation range (3-6).
 *  Round-5: the Foundry snapshot phase is an ALTERNATIVE phase source to the
 *  phase-progress subscription (which starts undefined and swallows subscribe
 *  failures) — the clock must arm off either, or a snapshot-only final review
 *  leaves the kernel escalation reading a frozen clock. */
export function phaseNumberInBuildEscalationRange(current: number | null | undefined): boolean {
  return typeof current === 'number' && current > 2 && current < 7;
}

export function phaseProgressInBuildEscalationRange(
  progress: PhaseProgressUpdate | null | undefined,
): boolean {
  return (
    progress?.kind === 'derived' && phaseNumberInBuildEscalationRange(progress.derived.current)
  );
}

/** Interval for the coarse escalation clock. 30s matches the pre-existing
 *  stale-build cadence; it only needs to be well below the 15-minute
 *  stale-build window and comfortably re-samples the 60s stamp stale-window. */
const BUILD_ESCALATION_CLOCK_INTERVAL_MS = 30_000;

/**
 * Returns a wall-clock `nowMs` that advances (every 30s) ONLY while the build
 * is in a stamp-gated escalation phase (3-6), and holds otherwise so idle
 * workspaces don't run a needless timer. Feed it to `deriveWorkspaceRunState`
 * as `signals.nowMs`.
 */
export function useBuildEscalationClock(
  phaseProgress: PhaseProgressUpdate | null | undefined,
  snapshotPhaseCurrent?: number | null,
): number {
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Arm on the EFFECTIVE phase: the phase-progress subscription when it has a
  // derived phase, OR the Foundry snapshot phase when it doesn't (degraded
  // subscription). Either being in 3-6 must advance the clock, or the
  // snapshot-only final-review path (round-5) leaves the kernel escalation
  // guard reading a frozen nowMs and suppressing a dead build forever.
  const active =
    phaseProgressInBuildEscalationRange(phaseProgress) ||
    phaseNumberInBuildEscalationRange(snapshotPhaseCurrent);
  useEffect(() => {
    if (!active) return undefined;
    setNowMs(Date.now());
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, BUILD_ESCALATION_CLOCK_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [active]);
  return nowMs;
}
